import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Platform, ScrollView, Dimensions } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useSelector } from 'react-redux';
import DLOButton from '../DLOButton';
import DLOModal from '../DLOModal';
import DLOTooltip from '../DLOTooltip';
import { createApiClient } from '../../../../api/apiClient';
import type { RootState } from '../../../../store';
import { useTheme } from '../../../../theme/useTheme';

interface VolumeTableRow {
  id: string;
  label: string;
  workTypes: Record<string, { default: string; override: string }>;
  __focused?: { [workType: string]: boolean };
  __changed?: { [workType: string]: boolean }; // Track changed override values for bold
  __manuallyModified?: { [workType: string]: boolean }; // Track which work types user manually modified
}

interface DLOVolumeForecastProps {
  data?: any;
  value?: VolumeTableRow[];
  onChange?: (data: VolumeTableRow[]) => void;
  onSave?: (data: VolumeTableRow[]) => void;
  onRowSave?: (rowId: string) => void;
  onRowClear?: (rowId: string) => void;
  onRowRestoreDefaults?: (rowId: string) => void;
  style?: any;
  isLoading?: boolean;
  // API parameters for save
  locId?: string;
  year?: number;
  period?: number;
  week?: number;
}

// Save Icon
const SaveIcon = ({ color = '#3A3A3A' }: { color?: string }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path
      d="M14.1667 2.5H4.16667C3.24167 2.5 2.5 3.25 2.5 4.16667V15.8333C2.5 16.75 3.24167 17.5 4.16667 17.5H15.8333C16.75 17.5 17.5 16.75 17.5 15.8333V5.83333L14.1667 2.5ZM15.8333 15.8333H4.16667V4.16667H13.475L15.8333 6.525V15.8333ZM10 10C8.61667 10 7.5 11.1167 7.5 12.5C7.5 13.8833 8.61667 15 10 15C11.3833 15 12.5 13.8833 12.5 12.5C12.5 11.1167 11.3833 10 10 10ZM5 5H12.5V8.33333H5V5Z"
      fill={color}
    />
  </Svg>
);

// Clear Icon
const ClearIcon = ({ color = '#2D2D2D' }: { color?: string }) => (
  <Svg width="17" height="15" viewBox="0 0 17 15" fill="none">
    <Path d="M12.6773 12.5H16.6356V14.1667H11.0106L12.6773 12.5ZM2.26064 14.1667L0.489805 12.3958C0.170361 12.0764 0.00716608 11.6806 0.000221631 11.2083C-0.00672281 10.7361 0.149527 10.3333 0.468972 10L9.63564 0.5C9.95508 0.166667 10.3474 0 10.8127 0C11.278 0 11.6704 0.159722 11.9898 0.479167L16.1356 4.625C16.4551 4.94444 16.6148 5.34028 16.6148 5.8125C16.6148 6.28472 16.4551 6.68056 16.1356 7L9.13564 14.1667H2.26064ZM8.42731 12.5L14.969 5.79167L10.844 1.66667L1.63564 11.1667L2.96897 12.5H8.42731Z" fill={color} />
  </Svg>
);

// History Icon
const HistoryIcon = ({ color = '#3A3A3A' }: { color?: string }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path
      d="M10.8335 2.5C6.69183 2.5 3.3335 5.85833 3.3335 10H0.833496L4.07516 13.2417L4.1335 13.3583L7.50016 10H5.00016C5.00016 6.775 7.6085 4.16667 10.8335 4.16667C14.0585 4.16667 16.6668 6.775 16.6668 10C16.6668 13.225 14.0585 15.8333 10.8335 15.8333C9.22516 15.8333 7.76683 15.175 6.71683 14.1167L5.5335 15.3C6.89183 16.6583 8.7585 17.5 10.8335 17.5C14.9752 17.5 18.3335 14.1417 18.3335 10C18.3335 5.85833 14.9752 2.5 10.8335 2.5ZM10.0002 6.66667V10.8333L13.5418 12.9333L14.1835 11.8667L11.2502 10.125V6.66667H10.0002Z"
      fill={color}
    />
  </Svg>
);

const DLOVolumeForecast: React.FC<DLOVolumeForecastProps> = ({
  data,
  value,
  onChange,
  onSave,
  onRowSave,
  onRowClear,
  onRowRestoreDefaults,
  style,
  isLoading = false,
  locId,
  year,
  period,
  week,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  // Get parameters from Redux store
  const selectedLocationId = useSelector((state: RootState) => state.appData?.selectedLocation?.id);
  const createSchedulePeriod = useSelector((state: RootState) => state.dlo?.createSchedule?.period);
  const createScheduleWeek = useSelector((state: RootState) => state.dlo?.createSchedule?.week);
  const createScheduleYear = useSelector((state: RootState) => state.dlo?.createSchedule?.year);
  // Calculate parameters for API call
  const getApiParameters = () => {
    // 1. loc_id from Redux store or fallback to prop
    const apiLocId = selectedLocationId || locId || "1016";
    
    // 2. year - current year or prop
    const apiYear = createScheduleYear || year ;
    
    // 3. period from Redux store or fallback to prop
    const apiPeriod = createSchedulePeriod || period || 8;
    
    // 4. week calculation: (period - 1) * 4 + 1
    return {
      loc_id: String(apiLocId), // Ensure it's a string for API
      year: apiYear,
      period: apiPeriod,
      week: createScheduleWeek
    };
  };
  // Determine available work types from data
  const getWorkTypesFromData = (apiData: any[]): string[] => {
    if (!apiData || !Array.isArray(apiData)) return ['Sales Volume', 'RECEIVING', 'REPLEN', 'TRANSFULL', 'TRANSPICK'];
    
    const uniqueWorkTypes = Array.from(new Set(apiData.map(item => item.workType)));
    return uniqueWorkTypes.length > 0 ? uniqueWorkTypes : ['Sales Volume', 'RECEIVING', 'REPLEN', 'TRANSFULL', 'TRANSPICK'];
  };

  const workTypes = value && value.length > 0 
    ? Object.keys(value[0]?.workTypes || {})
    : getWorkTypesFromData(data);

  // Initialize table data from props or default
  const initializeTableData = (): VolumeTableRow[] => {
    // If we have value from parent BUT no data from API yet, use the value
    // This prevents reinitializing when parent passes stale Redux state
    if (value && value.length > 0 && !data) {
      return value;
    }
    
    // If we have data from API, always reinitialize from API data
    // This ensures we show the latest fcastedCases when overrideCases is null

    // Create work types object with default values for default row
    const defaultWorkTypesObject: Record<string, { default: string; override: string }> = {};
    // Create work types object with override values for override row
    const overrideWorkTypesObject: Record<string, { default: string; override: string }> = {};
    
    console.log('Starting to build work types objects...');
    
    workTypes.forEach(workType => {
      // Find matching data from API
      const apiItem = data?.find((item: any) => item.workType === workType);
      console.log(`Looking for workType="${workType}" in data:`, apiItem);
      
      const fcastedCases = apiItem?.fcastedCases?.toString() || '0';
      const overrideCases = apiItem?.overrideCases;
            
      // Default row - always shows fcastedCases
      defaultWorkTypesObject[workType] = {
        default: fcastedCases,
        override: fcastedCases
      };
      
      // Override row - shows overrideCases if not null, otherwise fcastedCases
      const overrideValue = overrideCases !== null && overrideCases !== undefined ? overrideCases.toString() : fcastedCases;
      overrideWorkTypesObject[workType] = {
        default: fcastedCases,
        override: overrideValue
      };
      
      console.log(`  ✓ ${workType}: default="${fcastedCases}", override="${overrideValue}"`);
    });

    const result = [
      {
        id: 'default',
        label: 'Default Volume Forecast by Week',
        workTypes: defaultWorkTypesObject,
      },
      {
        id: 'override',
        label: 'Override Volume Forecast by Week',
        workTypes: overrideWorkTypesObject,
        __manuallyModified: {}, // Initialize with no manual modifications
      },
    ];    
    return result;
  };

  const [tableData, setTableData] = useState<VolumeTableRow[]>(initializeTableData);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Ref to track if we've already shown the success modal for this save
  const modalShownRef = useRef(false);
  
  // Track the original API data to detect manual changes
  const originalApiDataRef = useRef<any[]>([]);
  
  // Ref to prevent reinitialization after save
  const justSavedRef = useRef(false);

  // Update table data when props change
  useEffect(() => {
    // Don't reinitialize if we just saved
    if (justSavedRef.current) {
      console.log('DLOVolumeForecast: Skipping reinitialization after save');
      justSavedRef.current = false;
      return;
    }
    
    if (data || value) {      
      const newTableData = initializeTableData();      
      setTableData(newTableData);
      
      // Store original API data for comparison
      if (data && Array.isArray(data)) {
        originalApiDataRef.current = data;
        console.log('DLOVolumeForecast: Stored original API data for comparison');
      }
    }
  }, [data, value]);

  // Add __changed tracking for override values
  const handleInputChange = (rowId: string, workType: string, field: 'default' | 'override', inputValue: string) => {
    if (rowId === 'default') return;
    const numericValue = inputValue.replace(/[^0-9]/g, '');
    setTableData(prev => {
      const updatedData = prev.map(row => {
        if (row.id === rowId) {
          // Check if this is truly a manual change by comparing with original fcastedCases
          const originalApiItem = originalApiDataRef.current.find((item: any) => item.workType === workType);
          const originalFcastedCases = originalApiItem?.fcastedCases?.toString() || '0';
          const isManualChange = numericValue !== originalFcastedCases;
          
          // Track changed override value for bold
          let changed = row.__changed || {};
          if (field === 'override') {
            const original = row.workTypes[workType].default;
            changed = { ...changed, [workType]: numericValue !== original };
          }
          return {
            ...row,
            workTypes: {
              ...row.workTypes,
              [workType]: {
                ...row.workTypes[workType],
                [field]: numericValue
              }
            },
            __changed: changed,
            __manuallyModified: {
              ...row.__manuallyModified,
              [workType]: isManualChange,
            },
          };
        }
        return row;
      });
      
      // Check if there are any manual modifications
      const overrideRow = updatedData.find(row => row.id === 'override');
      const hasManualModifications = overrideRow?.__manuallyModified && 
        Object.values(overrideRow.__manuallyModified).some(modified => modified === true);
      
      // Mark that there are unsaved changes only if there are manual modifications
      setHasUnsavedChanges(hasManualModifications || false);
      setIsSaved(false);
      
      // DO NOT call onChange during typing - this causes infinite loop with Redux updates
      // The parent will get the updated data on save
      // onChange?.(updatedData);
      return updatedData;
    });
  };

  const handleSave = async () => {
    // Prevent multiple save attempts
    if (isSaving || showSuccessModal || modalShownRef.current) {
      return;
    }
    
    // Get API parameters from Redux store and calculations
    const apiParams = getApiParameters();
    
    setIsSaving(true);
    modalShownRef.current = false; // Reset the flag before starting save
    
    try {
      const apiClient = createApiClient();
      
      // Get override row data
      const overrideRow = tableData.find(row => row.id === 'override');
      if (!overrideRow) {
        throw new Error('Override row not found');
      }

      // Check if any workType has been manually modified
      const hasAnyManualModifications = overrideRow.__manuallyModified && 
        Object.values(overrideRow.__manuallyModified).some(modified => modified === true);

      if (!hasAnyManualModifications) {
        console.warn('No manually modified work types to save');
        setIsSaving(false);
        return;
      }

      // If any workType is modified, send ALL workTypes in the payload
      const requestData = workTypes.map(workType => {
        return {
          workType,
          overrideCases: parseInt(overrideRow.workTypes[workType]?.override || '0', 10),
        };
      });

      const payload = {
        ...apiParams,
        data: requestData
      };

      console.log('Saving Volume Forecast with payload:', payload);
      console.log('Sending all workTypes since user made changes:', requestData.map(d => d.workType));

      const response = await apiClient.put('/dlo/schedule-forecast', payload);

      if (response.data?.success) {
        console.log('Volume Forecast saved successfully:', response.data);
        
        // Set flag to prevent useEffect from reinitializing
        justSavedRef.current = true;
        
        setIsSaved(true);
        setHasUnsavedChanges(false); // Reset unsaved changes flag after successful save
        
        // Create updated table data with the saved values from API response
        const updatedTableData = tableData.map(row => {
          if (row.id === 'override') {
            // Update override values with the saved data from API
            const updatedWorkTypes = { ...row.workTypes };
            
            if (response.data?.data?.updates) {
              response.data.data.updates.forEach((update: { workType: string; overrideCases: number }) => {
                if (updatedWorkTypes[update.workType]) {
                  const savedValue = update.overrideCases.toString();
                  updatedWorkTypes[update.workType] = {
                    ...updatedWorkTypes[update.workType],
                    override: savedValue
                  };
                  console.log(`✓ Updated ${update.workType} override to ${savedValue}`);
                }
              });
              
              // Update originalApiDataRef with the saved values
              originalApiDataRef.current = response.data.data.updates.map((update: { workType: string; overrideCases: number }) => ({
                workType: update.workType,
                fcastedCases: parseInt(updatedWorkTypes[update.workType]?.default || '0', 10),
                overrideCases: update.overrideCases
              }));
              console.log('✓ Updated originalApiDataRef with saved values');
            }
            
            return {
              ...row,
              workTypes: updatedWorkTypes,
              __manuallyModified: {}, // Reset manual modification tracking
              __changed: {}, // Reset changed tracking
            };
          }
          return row;
        });
        
        console.log('✓ Setting updated table data:', updatedTableData);
        
        // Update state with the new data
        setTableData(updatedTableData);
        
        // Only show modal if we haven't already shown it
        if (!modalShownRef.current) {
          modalShownRef.current = true;
          setShowSuccessModal(true);
        }
        
        // Notify parent with updated table data after save
        onSave?.(updatedTableData);
      } else {
        throw new Error(response.data?.message || 'Save failed');
      }
    } catch (error) {
      console.error('Error saving Volume Forecast:', error);
      modalShownRef.current = false; // Reset flag on error
      // You can add error handling UI here if needed
    } finally {
      setIsSaving(false);
    }
  };

  const handleRowSave = (rowId: string) => {
    console.log('Volume Forecast row saved:', rowId);
    onRowSave?.(rowId);
  };

  const handleClear = () => {
    const clearedData = tableData.map(row => ({
      ...row,
      workTypes: Object.keys(row.workTypes).reduce((acc, workType) => ({
        ...acc,
        [workType]: { default: row.workTypes[workType].default, override: '' }
      }), {}),
      __manuallyModified: row.id === 'override' ? {} : row.__manuallyModified, // Clear manual modifications for override row
    }));
    
    setTableData(clearedData);
    setHasUnsavedChanges(false); // No changes after clearing
    setIsSaved(false);
    // Notify parent only on intentional clear action
    onChange?.(clearedData);
    console.log('Volume Forecast table cleared');
  };

  const handleRowClear = (rowId: string) => {
    const clearedData = tableData.map(row =>
      row.id === rowId 
        ? {
            ...row,
            workTypes: Object.keys(row.workTypes).reduce((acc, workType) => ({
              ...acc,
              [workType]: { default: row.workTypes[workType].default, override: '' }
            }), {}),
            __manuallyModified: {}, // Clear manual modifications when row is cleared
          }
        : row
    );
    
    setTableData(clearedData);
    setHasUnsavedChanges(false); // No changes after clearing
    setIsSaved(false);
    // Notify parent only on intentional clear action
    onChange?.(clearedData);
    onRowClear?.(rowId);
  };

  const handleRestoreDefaults = (rowId: string) => {
    // Only handle override row
    if (rowId !== 'override') {
      return;
    }
    
    const updatedData = tableData.map(row => {
      if (row.id === 'override') {
        // Find the default row to get default values
        const defaultRow = tableData.find(r => r.id === 'default');
        if (!defaultRow) return row;
        
        // Create new workTypes object with default values copied to override fields
        const updatedWorkTypes = { ...row.workTypes };
        const manuallyModified: { [key: string]: boolean } = {};
        
        workTypes.forEach(workType => {
          const defaultValue = defaultRow.workTypes[workType]?.default || '';
          updatedWorkTypes[workType] = {
            ...updatedWorkTypes[workType],
            override: defaultValue
          };
          
          // Mark all work types as manually modified when restore defaults is clicked
          const originalApiItem = originalApiDataRef.current.find((item: any) => item.workType === workType);
          const originalFcastedCases = originalApiItem?.fcastedCases?.toString() || '0';
          manuallyModified[workType] = defaultValue !== originalFcastedCases;
        });
        
        return {
          ...row,
          workTypes: updatedWorkTypes,
          __manuallyModified: manuallyModified,
        };
      }
      return row;
    });
    
    setTableData(updatedData);
    
    // Check if any values are actually different from original
    const overrideRow = updatedData.find(row => row.id === 'override');
    const hasActualChanges = overrideRow?.__manuallyModified && 
      Object.values(overrideRow.__manuallyModified).some(modified => modified === true);
    
    setHasUnsavedChanges(hasActualChanges || false);
    setIsSaved(false);
    // Notify parent only on intentional restore action
    onChange?.(updatedData);
    onRowRestoreDefaults?.(rowId);
    console.log('Restored default values to override fields for row:', rowId);
  };

  // Check if all override inputs are filled
  const areAllOverrideInputsFilled = (): boolean => {
    const overrideRow = tableData.find(row => row.id === 'override');
    if (!overrideRow) return false;
    
    return workTypes.some(workType => {
      const overrideValue = overrideRow.workTypes[workType]?.override || '';
      // Convert to string and then check if it's not empty
      const stringValue = String(overrideValue).trim();
      return stringValue !== '';
    });
  };

  // Save button is enabled only when there are manual modifications and no pending save
  const isSaveButtonEnabled = areAllOverrideInputsFilled() && hasUnsavedChanges && !isSaved;

  if (isLoading) {
    return (
      <View style={[styles.container, style, styles.loadingContainer]}>
        <Text style={styles.loadingText}>Loading Volume Forecast...</Text>
      </View>
    );
  }

  return (
    <ScrollView 
      horizontal 
      showsHorizontalScrollIndicator={Platform.OS === 'web'}
      contentContainerStyle={styles.scrollContainer}
    >
      <View style={[styles.container, style]}>
        {/* Table */}
        <View style={styles.table}>
          {/* Header Row */}
          <View style={styles.headerRow}>
            <View style={[styles.cell, styles.labelHeader]}>
              <Text style={styles.headerText}>Weekly Volume Distribution</Text>
            </View>
            {workTypes.map((workType) => (
              <View key={workType} style={[styles.cell, styles.columnHeader]}>
                <Text style={styles.headerText}>{workType.toUpperCase()}</Text>
              </View>
            ))}
            <View style={[styles.cell, styles.actionHeader]}>
              <Text style={styles.headerText}>ACTION</Text>
            </View>
          </View>

          {/* Data Rows */}
          {tableData.map((row) => (
            <View key={row.id} style={styles.dataRow}>
              <View style={[styles.cell, styles.labelCell]}>
                <Text style={styles.labelText}>{row.label}</Text>
              </View>
              {workTypes.map((workType) => (
                <View key={workType} style={[styles.cell, styles.inputCell, isDark && { backgroundColor: colors.background.elevated }]}>
                  <TextInput
                    style={[
                      styles.input,
                      isDark && {
                        backgroundColor: colors.background.elevated,
                        color: colors.text.primary,
                        borderColor: colors.border.default,
                      },
                      row.id === 'default' && (isDark ? { backgroundColor: '#1A1A1A', color: colors.text.secondary } : styles.disabledInput),
                      row.id === 'override' && row.__changed && row.__changed[workType] ? { fontWeight: 'bold' } : { fontWeight: '400' }
                    ]}
                    value={row.id === 'default' 
                      ? (parseInt(row.workTypes[workType]?.default || '0', 10) || 0).toLocaleString()
                      : (() => {
                          // For override row, use the override value directly (which already has fcastedCases when overrideCases is null)
                          const overrideValue = row.workTypes[workType]?.override || '0';
                          const numericValue = parseInt(overrideValue, 10) || 0;
                          return numericValue.toLocaleString();
                        })()
                    }
                    onFocus={() => {
                      if (row.id !== 'default') {
                        setTableData(prev => prev.map(r => r.id === row.id ? { ...r, __focused: { ...(r.__focused || {}), [workType]: true } } : r));
                      }
                    }}
                    onBlur={() => {
                      if (row.id !== 'default') {
                        setTableData(prev => prev.map(r => r.id === row.id ? { ...r, __focused: { ...(r.__focused || {}), [workType]: false } } : r));
                      }
                    }}
                    onChangeText={(value) => handleInputChange(
                      row.id, 
                      workType, 
                      row.id === 'default' ? 'default' : 'override', 
                      value
                    )}
                    keyboardType="numeric"
                    textAlign="center"
                    editable={row.id !== 'default'}
                    pointerEvents={row.id === 'default' ? 'none' : 'auto'}
                    placeholder={row.id === 'default' ? '' : ''}
                  />
                </View>
              ))}
              <View style={[styles.cell, styles.actionCell]}>
                {/* Hide action buttons for default row */}
                {row.id !== 'default' && (
                  <View style={styles.actionButtons}>
                    {/* <View style={styles.tooltipWrapper}>
                      <DLOTooltip 
                        content="Save" 
                        position="top" 
                        trigger="hover" 
                        verticalOffset={30}
                        horizontalOffset={-10}
                        maxWidth={40}
                        minWidth={60}
                        padding={6}
                        textAlign="center"
                      >
                        <TouchableOpacity 
                          style={styles.actionButton} 
                          onPress={() => handleRowSave(row.id)}
                        >
                          <SaveIcon />
                        </TouchableOpacity>
                      </DLOTooltip>
                    </View> */}
                    <View style={styles.tooltipWrapper}>
                      <DLOTooltip 
                        content="Clear" 
                        position="top" 
                        trigger="hover" 
                        verticalOffset={30}
                        horizontalOffset={-10}
                        maxWidth={40}
                        minWidth={60}
                        padding={6}
                        textAlign="center"
                      >
                        <TouchableOpacity
                          style={styles.actionButton}
                          onPress={() => handleRowClear(row.id)}
                        >
                          <ClearIcon color={isDark ? '#FFFFFF' : '#2D2D2D'} />
                        </TouchableOpacity>
                      </DLOTooltip>
                    </View>
                    <View style={styles.tooltipWrapper}>
                      <DLOTooltip 
                        content="Defaults" 
                        position="top" 
                        trigger="hover" 
                        verticalOffset={30}
                        horizontalOffset={-10}
                        maxWidth={80}
                        minWidth={100}
                        padding={6}
                        textAlign="center"
                      >
                        <TouchableOpacity
                          style={styles.actionButton}
                          onPress={() => handleRestoreDefaults(row.id)}
                        >
                          <HistoryIcon color={isDark ? '#FFFFFF' : '#3A3A3A'} />
                        </TouchableOpacity>
                      </DLOTooltip>
                    </View>
                  </View>
                )}
              </View>
            </View>
          ))}
        </View>

        {/* Save Button */}
        <View style={styles.saveButtonContainer}>
          <DLOButton
            text={isSaved ? "Saved" : "Save"}
            variant="primary"
            size="medium"
            onPress={handleSave}
            isLoading={isSaving}
            disabled={!isSaveButtonEnabled || isSaved}
          />
        </View>
      </View>

      {/* Success Modal */}
      <DLOModal
        visible={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          modalShownRef.current = false; // Reset the modal shown flag
        }}
        title="Success"
        hideButtons={true}
      >
        <View style={styles.modalContent}>
          <Text style={styles.modalText}>Volume Forecast saved successfully</Text>
          <View style={styles.modalButtonContainer}>
            <DLOButton
              text="OK"
              variant="primary"
              size="medium"
              onPress={() => {
                setShowSuccessModal(false);
                modalShownRef.current = false; // Reset the modal shown flag
              }}
              style={styles.modalOkButton}
            />
          </View>
        </View>
      </DLOModal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    flexGrow: 1,
    minWidth: '100%',
  },
  container: {
    borderRadius: 8,
    backgroundColor: '#FBFCFE',
    borderWidth: 1,
    borderColor: '#E0E0E0',
    overflow: 'hidden',
    width: Platform.select({
      web: '90%',
      default: '100%',
    }),
    minWidth: Platform.select({
      web: 720,
      default: 540,
    }),
  },
  loadingContainer: {
    padding: 40,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  loadingText: {
    fontSize: 14,
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    textAlign: 'center',
  },
  table: {
    borderWidth: 0, // Remove border since container has it
  },
  headerRow: {
    flexDirection: 'row',
    backgroundColor: '#005CBC',
    height: Platform.select({
      web: 48,
      default: 56,
    }),
  },
  dataRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  cell: {
    padding: 12,
    borderRightWidth: 1,
    borderRightColor: '#E0E0E0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  labelHeader: {
    flex: 2,
    backgroundColor: '#005CBC',
    padding: 9,
  },
  columnHeader: {
    flex: 1.2,
    backgroundColor: '#005CBC',
    padding: 9,
  },
  actionHeader: {
    flex: 0.8,
    backgroundColor: '#005CBC',
    padding: 9,
  },
  labelCell: {
    flex: 2,
    alignItems: 'flex-start',
    paddingLeft: 16,
  },
  inputCell: {
    flex: 1.2,
    backgroundColor: '#FBFCFE',
  },
  actionCell: {
    flex: 0.8,
    backgroundColor: '#FBFCFE',
    borderRightWidth: 0,
  },
  headerText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 12,
    textAlign: 'center',
    textTransform: 'uppercase',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  labelText: {
    color: '#3A3A3A',
    fontSize: 12,
    fontWeight: 'bold',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  input: {
    width: '100%',
    maxWidth: '40%',
    minHeight: Platform.select({
      web: 28,
      default: 32,
    }),
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    paddingHorizontal: Platform.select({
      web: 8,
      default: 12,
    }),
    paddingVertical: Platform.select({
      web: 4,
      default: 6,
    }),
    fontSize: Platform.select({
      web: 12,
      default: 14,
    }),
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontWeight: '400',
    lineHeight: Platform.select({
      web: 18,
      default: 20,
    }),
    textAlign: 'center',
    color: '#3A3A3A',
    backgroundColor: '#FFFFFF',
    minWidth: Platform.select({
      web: 65,
      default: 45,
    }),
  },
  disabledInput: {
    backgroundColor: '#D0D0D0',
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    padding: 4,
  },
  tooltipWrapper: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveButtonContainer: {
    padding: 16,
    alignItems: 'flex-end',
    backgroundColor: '#FBFCFE',
  },
  modalContent: {
    padding: 20,
    alignItems: 'center',
  },
  modalText: {
    fontSize: 16,
    color: '#3A3A3A',
    textAlign: 'center',
    marginBottom: 20,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  modalButtonContainer: {
    width: '100%',
    alignItems: 'center',
  },
  modalOkButton: {
    minWidth: 80,
  },
});

export default DLOVolumeForecast;
